import { Group, SimpleGrid, Text } from "@mantine/core";
import { IconChartBar, IconClock, IconCoins, IconReceipt2, IconWallet } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { DashboardData } from "../../../apis/myDashboard";
import { formatDate, formatMoney } from "../../../utils/format";
import { MoneyCell } from "../MoneyCell";
import { KpiTile } from "./KpiTile";

interface Props {
  data?: DashboardData;
  loading: boolean;
  currency: string;
}

// ROW 1 — the five headline KPIs (UI-SPEC §3). ЦЭВЭР ОРЛОГО is the emphasized,
// sign-driven tile; collected fees and avg/txn carry period-over-period trends.
export function KpiRow({ data, loading, currency }: Props) {
  const navigate = useNavigate();
  const k = data?.kpis;
  const netPositive = Number(k?.netIncome.value ?? 0) >= 0;
  const overdueCount = k?.overdue.count ?? 0;

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="md">
      <KpiTile
        loading={loading}
        icon={<IconCoins size={20} />}
        label="Цуглуулсан шимтгэл"
        value={<MoneyCell value={k?.collectedFees.value ?? 0} currency={currency} />}
        trend={k?.collectedFees.deltaPct}
        trendSuffix="өмнөх үетэй"
        sub={`${k?.collectedFees.count ?? 0} гүйлгээ`}
        onClick={() => navigate("/my-billing/collected-fees")}
      />
      <KpiTile
        loading={loading}
        icon={<IconChartBar size={20} />}
        label="Дундаж / гүйлгээ"
        value={<MoneyCell value={k?.avgPerTxn.value ?? 0} currency={currency} />}
        trend={k?.avgPerTxn.deltaPct}
        sub={`${k?.collectedFees.count ?? 0} гүйлгээ`}
        onClick={() => navigate("/my-billing/collected-fees")}
      />
      <KpiTile
        loading={loading}
        icon={<IconReceipt2 size={20} />}
        label="Платформд төлөх"
        value={(
          <Group gap={4} wrap="nowrap" align="baseline">
            <Text span c="dimmed">~</Text>
            <MoneyCell value={k?.platformCharge.estimate ?? 0} currency={currency} />
          </Group>
        )}
        sub={k?.platformCharge.nextInvoiceDate
          ? `Дараагийн нэхэмжлэх: ${formatDate(k.platformCharge.nextInvoiceDate)}`
          : "Тооцоолсон дүн"}
        onClick={() => navigate("/my-billing/platform?tab=invoices")}
      />
      <KpiTile
        loading={loading}
        icon={<IconWallet size={20} />}
        label="Цэвэр орлого"
        accent={netPositive ? "green" : "red"}
        value={<MoneyCell value={k?.netIncome.value ?? 0} currency={currency} c={netPositive ? "green" : "red"} fw={700} />}
        sub="Шимтгэл − платформ зардал"
        onClick={() => navigate("/my-billing/platform?tab=invoices")}
      />
      <KpiTile
        loading={loading}
        icon={<IconClock size={20} />}
        label="Хугацаа хэтэрсэн"
        value={overdueCount > 0
          ? <Text span fw={700} c="red">{overdueCount}</Text>
          : <Text span>0</Text>}
        sub={overdueCount > 0
          ? <Text span size="xs" c="red">{formatMoney(k?.overdue.amount ?? 0, currency)} төлбөртэй</Text>
          : <Text span size="xs" c="green">Цэвэр</Text>}
        onClick={() => navigate("/my-billing/platform?tab=invoices")}
      />
    </SimpleGrid>
  );
}
